import {
  Checkbox,
  Drawer,
  Flex,
  Image,
  Popconfirm,
  Tag,
  Typography,
} from 'antd';
import {
  Container,
  Description,
  ImageContainer,
  CauseName,
  ActionButtonWrapper,
} from './style';
import { Cause, QueryType, SuggestionStatus, YesOrNo } from '../../types';
import { useTranslation } from 'react-i18next';
import Icon from '@ant-design/icons';
import { SVGDelete, SVGEdit } from '../../images';
import SuggestionTag from '../issueCard/suggestionTag';
import { useState } from 'react';
import SuggestionReviewDrawer from '../suggestionReviewDrawer';
import { useAppDispatch } from '../../store';
import { fetchCauses } from '../../slices/causes';
import { fetchTemplateCauses } from '../../slices/templates';
import CauseForm from '../causeForm';

const CauseCard = ({
  cause,
  checkable,
  checked,
  onCheck,
  showEdit,
  showDelete,
  onEdit,
  onDelete,
  showSuggestionStatus,
  showLinkedBadge,
  isLinked,
}: {
  cause: Cause;
  checked?: boolean;
  checkable?: boolean;
  onCheck?: (checked: boolean) => void;
  showEdit?: boolean;
  showDelete?: boolean;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  showSuggestionStatus?: boolean;
  showLinkedBadge?: boolean;
  isLinked?: boolean;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const id = cause.causeID;
  const [suggestionReviewDrawerVisible, setSuggestionReviewDrawerVisible] =
    useState(false);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);

  const handleSuggestionReviewDrawerClose = (isSubmitted?: boolean) => {
    setSuggestionReviewDrawerVisible(false);
    if (isSubmitted) {
      dispatch(fetchTemplateCauses({ templateId: String(cause.templateID) }));
    }
  };

  const handleClick = (e: any) => {
    if (onCheck) {
      return;
    }
    if (cause.suggestionStatus === SuggestionStatus.SUBMITTED) {
      setSuggestionReviewDrawerVisible(true);
    } else {
      setShowDetailsDrawer(true);
    }
  };

  return (
    <Flex flex={1} align="center">
      <Container
        vertical
        gap={8}
        className={`${checked ? 'checked' : ''} ${checkable ? 'checkable' : ''}`}
        onClick={() => onCheck && onCheck(!checked)}
      >
        <Flex gap={8} onClick={handleClick}>
          {checkable && (
            <Checkbox
              checked={checked}
              onChange={(e) => onCheck && onCheck(e.target.checked)}
            ></Checkbox>
          )}
          <ImageContainer>
            <Image
              src={cause.images?.[0]?.url}
              preview={false}
              fallback="/placeholder.png"
            />
          </ImageContainer>
          <Flex vertical flex={1}>
            <CauseName>{cause.name}</CauseName>
          </Flex>
          {showSuggestionStatus && cause.suggestionStatus && (
            <SuggestionTag status={cause.suggestionStatus} />
          )}
          {showLinkedBadge && isLinked && (
            <div>
              <Tag color="cyan" bordered={false}>
                Linked in Solution Grid
              </Tag>
            </div>
          )}
        </Flex>
        {cause.description && <Description>{cause.description}</Description>}
        {showSuggestionStatus && (
          <Flex gap={16}>
            {cause.isSuggestion === YesOrNo.YES && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Submitted By: {cause?.suggestionSubmitterName || '-'} {'    '}{' '}
                at {cause?.suggestionSubmittedTime?.replace('T', ' ') || '-'}
              </Typography.Text>
            )}
            {cause.suggestionStatus === SuggestionStatus.APPROVED && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Accepted By: {cause?.suggestionReviewerName || '-'} {'    '} at{' '}
                {cause?.suggestionReviewTime?.replace('T', ' ') || '-'}
              </Typography.Text>
            )}
            {cause.suggestionStatus === SuggestionStatus.REJECTED && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Rejected By: {cause?.suggestionReviewerName || '-'} {'    '} at{' '}
                {cause?.suggestionReviewTime?.replace('T', ' ') || '-'}
              </Typography.Text>
            )}
          </Flex>
        )}
      </Container>
      <Flex gap={8}>
        {showEdit && (
          <ActionButtonWrapper
            onClick={(e) => onEdit?.(id)}
            style={{ cursor: 'pointer' }}
            title={t('edit')}
          >
            <Icon component={SVGEdit} />
          </ActionButtonWrapper>
        )}

        {showDelete && (
          <Popconfirm
            placement="bottom"
            title={`Are you sure you want to delete ${cause.name}`}
            okText="Yes"
            cancelText="No"
            onConfirm={(e) => onDelete?.(id)}
          >
            <ActionButtonWrapper
              style={{ cursor: 'pointer' }}
              title={t('delete')}
            >
              <Icon component={SVGDelete} />
            </ActionButtonWrapper>
          </Popconfirm>
        )}
      </Flex>
      <SuggestionReviewDrawer
        visible={suggestionReviewDrawerVisible}
        onClose={handleSuggestionReviewDrawerClose}
        queryType={QueryType.CASUE}
        data={cause}
      />
      <Drawer
        title={`Cause-${cause?.name || ''}`}
        closable
        onClose={() => setShowDetailsDrawer(false)}
        open={showDetailsDrawer}
        width={800}
        footer={null}
      >
        <CauseForm cause={cause as Cause} readOnly />
      </Drawer>
    </Flex>
  );
};

export default CauseCard;
